import type { ListProductsQuery, Product } from '@clockwise/clients/products';
import type { ServiceRole } from '@clockwise/clients/service-roles';
import { ApiError, LifecycleStatus } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Download, Filter, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { DataTable } from '@/components/custom/data-table';
import {
  DEFAULT_KIND,
  KIND_FILTERS,
  KIND_LABELS,
  KindBadge,
  type KindFilter,
  kindFilterToQuery,
} from '@/components/custom/kind-filter';
import { LifecycleStatusBadge } from '@/components/custom/lifecycle-status-badge';
import { ProductFormSheet } from '@/components/custom/product-form-sheet';
import { ProductImportSheet } from '@/components/custom/product-import-sheet';
import { RequirePermission } from '@/components/custom/require-permission';
import { RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { ServiceRoleLabel, ServiceRoleList } from '@/components/custom/service-role-label';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { productsApi, serviceRolesApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';
import { warnIfTruncated } from '@/lib/warn-if-truncated';

const SORT_FIELD_VALUES = ['retainerCode', 'tmCode', 'name', 'status', 'createdAt'] as const;
type SortField = (typeof SORT_FIELD_VALUES)[number];

const STATUS_FILTERS = ['active', 'inactive', 'all'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const STATUS_LABELS: Record<StatusFilter, string> = {
  active: 'Active',
  inactive: 'Inactive',
  all: 'All statuses',
};

// Sentinel for the "no role filter" option — Radix Select can't use an empty
// string as an item value.
const ALL_ROLES = 'all';

const listSearch = z.object({
  q: z.string().optional(),
  page: z.coerce.number().int().min(0).optional(),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  status: z.enum(STATUS_FILTERS).optional(),
  kind: z.enum(KIND_FILTERS).optional(),
  role: z.string().uuid().optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/setup/activities/')({
  validateSearch: listSearch,
  component: ProductsPage,
});

const PAGE_SIZE = 25;
// Codes are optional/nullable — default to name so no-code activities don't
// all cluster together and pagination stays stable.
const DEFAULT_SORT_FIELD: SortField = 'name';
const DEFAULT_STATUS: StatusFilter = 'active';

function statusFilterToQuery(filter: StatusFilter): LifecycleStatus | undefined {
  if (filter === 'active') return LifecycleStatus.Active;
  if (filter === 'inactive') return LifecycleStatus.Inactive;
  return undefined;
}

function ProductsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const sortField: SortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection: 'asc' | 'desc' = search.sortDirection ?? 'asc';
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const statusFilter: StatusFilter = search.status ?? DEFAULT_STATUS;
  const kindFilter: KindFilter = search.kind ?? DEFAULT_KIND;
  const roleFilter = search.role;

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({
      search: (prev: ListSearch) => {
        const merged: ListSearch = { ...prev, ...next };
        return stripDefaults(merged);
      },
      replace: true,
    });

  const [editing, setEditing] = useState<Product | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();

  const exportMutation = useMutation({
    mutationFn: () =>
      productsApi.exportXlsx({
        status: statusFilterToQuery(statusFilter),
        internal: kindFilterToQuery(kindFilter),
        serviceRoleId: roleFilter,
        q: q.trim() || undefined,
      }),
    onSuccess: ({ blob, filename }) => {
      triggerDownload(blob, filename ?? 'activities.xlsx');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Export failed';
      toast.error(msg);
    },
  });

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const listQuery: ListProductsQuery = {
    q: q.trim() || undefined,
    status: statusFilterToQuery(statusFilter),
    internal: kindFilterToQuery(kindFilter),
    // Primary role only — Additional access is reachable through the search box.
    serviceRoleId: roleFilter,
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.products.list(listQuery),
    queryFn: () => productsApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  // Resolve service-role names for the Primary / Additional access columns,
  // and populate the service-role filter dropdown. Cache-shared with the
  // form/autocomplete via the same queryKey shape. 500 is the API max; the
  // role catalog is small, so this shows every role (warnIfTruncated below
  // is the backstop if a tenant ever exceeds it).
  const SERVICE_ROLES_LIST_QUERY = {
    sortField: 'name',
    sortDirection: 'asc',
    limit: 500,
  } as const;
  const serviceRolesQuery = useQuery({
    queryKey: queryKeys.serviceRoles.list(SERVICE_ROLES_LIST_QUERY),
    queryFn: () => serviceRolesApi.list(SERVICE_ROLES_LIST_QUERY),
  });
  warnIfTruncated(serviceRolesQuery.data, 'products-list service-role column');
  const roleById = useMemo(
    () => new Map((serviceRolesQuery.data?.data ?? []).map((r) => [r.id, r])),
    [serviceRolesQuery.data],
  );

  // A bookmarked ?role=<uuid> can outlive the role it points at (hard-deleted,
  // or a stale link from another tenant). Once the roles have loaded, drop a
  // filter that resolves to nothing — otherwise the dropdown silently shows
  // "All service roles" while the list stays filtered to an invisible role.
  useEffect(() => {
    if (serviceRolesQuery.isSuccess && roleFilter && !roleById.has(roleFilter)) {
      updateSearch({ role: undefined, page: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceRolesQuery.isSuccess, roleFilter, roleById]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => productsApi.remove(id),
    onSuccess: () => {
      toast.success('Activity deactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Deactivate failed';
      toast.error(msg);
    },
  });

  const columns = useMemo<ColumnDef<Product>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 170,
        cell: ({ row }) => (
          <Link
            to="/setup/activities/$id"
            params={{ id: row.original.id }}
            className="block font-medium break-words text-primary underline-offset-2 hover:underline"
            title={row.original.name}
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: 'internal',
        accessorKey: 'internal',
        header: 'Kind',
        size: 80,
        enableSorting: false,
        cell: ({ row }) => <KindBadge internal={row.original.internal} />,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        size: 80,
        cell: ({ row }) => <LifecycleStatusBadge status={row.original.status} />,
      },
      {
        id: 'primary',
        header: 'Primary',
        size: 140,
        enableSorting: false,
        cell: ({ row }) => {
          const role = roleById.get(row.original.serviceRoleId);
          // truncate={false}: these columns are narrow (140/160px), and a name
          // clipped to "Acc…" is worse than one that wraps to a second line —
          // the row is already sized by the wrapping Name column.
          return role ? (
            <ServiceRoleLabel code={role.shortCode || null} name={role.name} truncate={false} />
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      },
      {
        id: 'visibleTo',
        header: 'Additional access',
        size: 160,
        enableSorting: false,
        cell: ({ row }) => {
          const extras = row.original.visibleToServiceRoleIds
            .map((id) => roleById.get(id))
            .filter((r): r is ServiceRole => r !== undefined);
          if (extras.length === 0) {
            return <span className="text-muted-foreground">—</span>;
          }
          return <ServiceRoleList roles={extras} />;
        },
      },
      {
        accessorKey: 'retainerCode',
        header: 'Retainer code',
        size: 100,
        cell: ({ row }) => (
          <span className="block break-words" title={row.original.retainerCode ?? undefined}>
            {row.original.retainerCode ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'tmCode',
        header: 'T&M code',
        size: 100,
        cell: ({ row }) => (
          <span className="block break-words" title={row.original.tmCode ?? undefined}>
            {row.original.tmCode ?? '—'}
          </span>
        ),
      },
      {
        id: 'excludeFromBudget',
        accessorKey: 'excludeFromBudget',
        header: 'Budget',
        size: 90,
        // Not in SORT_FIELD_VALUES, so it must not render a sort toggle:
        // onSortingChange casts the column id straight into `sortField`, and
        // a value outside that enum fails the route's validateSearch. Same
        // reason the Kind column opts out.
        enableSorting: false,
        // Only the exclusion is worth a mark — "counted normally" is the
        // default for the overwhelming majority of rows, so rendering it
        // would be noise on every line.
        cell: ({ row }) =>
          row.original.excludeFromBudget ? (
            <span className="text-xs">Excluded</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'actions',
        header: '',
        size: 72,
        enableSorting: false,
        cell: ({ row }) => (
          <RequirePermission permission="activity.edit">
            <RowActions>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(row.original);
                  setSheetOpen(true);
                }}
              >
                <Pencil />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.original.status !== LifecycleStatus.Active) return;
                  if (
                    confirm(
                      `Deactivate "${row.original.name}"? Existing engagements continue to reference it.`,
                    )
                  ) {
                    deleteMutation.mutate(row.original.id);
                  }
                }}
                aria-label="Deactivate"
                disabled={
                  deleteMutation.isPending || row.original.status !== LifecycleStatus.Active
                }
              >
                <Trash2 />
              </Button>
            </RowActions>
          </RequirePermission>
        ),
      },
    ],
    [deleteMutation, roleById],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        updateSearch({ sortField: undefined, sortDirection: undefined });
        return;
      }
      const id = first.id as SortField;
      updateSearch({
        sortField: id,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));

  return (
    <PageShell>
      <PageHeader title="Activities" subtitle="The catalog of services offered." count={total}>
        <RequirePermission permission="activity.view">
          <Button
            variant="outline"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            <Download />
            {exportMutation.isPending ? 'Exporting…' : 'Export'}
          </Button>
        </RequirePermission>
        <RequirePermission permission="activity.edit">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload />
            Import
          </Button>
        </RequirePermission>
        <RequirePermission permission="activity.edit">
          <Button
            onClick={() => {
              setEditing(null);
              setSheetOpen(true);
            }}
          >
            <Plus />
            Add activity
          </Button>
        </RequirePermission>
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search name, role, additional access…"
          value={searchInput}
          onValueChange={setSearchInput}
        />
        <Select
          value={roleFilter ?? ALL_ROLES}
          onValueChange={(v) => updateSearch({ role: v === ALL_ROLES ? undefined : v, page: 0 })}
        >
          <SelectTrigger className="w-[200px]" aria-label="Service role">
            <SelectValue placeholder="All service roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_ROLES}>All service roles</SelectItem>
            {(serviceRolesQuery.data?.data ?? []).map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.shortCode} · {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={statusFilter}
          onValueChange={(v) =>
            updateSearch({
              status: (v as StatusFilter) === DEFAULT_STATUS ? undefined : (v as StatusFilter),
              page: 0,
            })
          }
        >
          <SelectTrigger className="w-[140px]" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((f) => (
              <SelectItem key={f} value={f}>
                {STATUS_LABELS[f]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={kindFilter}
          onValueChange={(v) =>
            updateSearch({
              kind: (v as KindFilter) === DEFAULT_KIND ? undefined : (v as KindFilter),
              page: 0,
            })
          }
        >
          <SelectTrigger className="w-[140px]" aria-label="Kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {KIND_FILTERS.map((f) => (
              <SelectItem key={f} value={f}>
                {KIND_LABELS[f]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        table={table}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage={
          query.error instanceof ApiError ? query.error.message : 'Failed to load activities.'
        }
        emptyMessage={
          q || roleFilter || statusFilter !== DEFAULT_STATUS || kindFilter !== DEFAULT_KIND
            ? 'No activities match these filters.'
            : 'No activities yet. Click “Add activity”.'
        }
        onRowClick={(p) => navigate({ to: '/setup/activities/$id', params: { id: p.id } })}
      />

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>

      <ProductFormSheet open={sheetOpen} onOpenChange={setSheetOpen} product={editing} />
      <ProductImportSheet open={importOpen} onOpenChange={setImportOpen} />
    </PageShell>
  );
}

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== 'asc') out.sortDirection = s.sortDirection;
  if (s.status && s.status !== DEFAULT_STATUS) out.status = s.status;
  if (s.kind && s.kind !== DEFAULT_KIND) out.kind = s.kind;
  if (s.role) out.role = s.role;
  return out;
}
